import { useField, useFormikContext } from 'formik';
import {
  FormikTextInputField,
  SegmentedControl,
  Select,
} from '@spotted-zebra-uk/ui-components';
import { STAGE_CATEGORY_LABELS } from '../../Projects.constants';
import StageTypeChip from '../StageTypeChip/StageTypeChip';
import styles from './ProjectFilterModal.module.scss';
import { FiltersFormValues } from './ProjectsFilterModal';
import {
  ASSESSMENT_STATUS_CATEGORY,
  ASSESSMENT_STATUS_OPTIONS,
  AssessmentStatusFilter,
  FilterableStageCategory,
  INTERVIEW_LINKED_CATEGORY,
  INTERVIEW_LINKED_OPTIONS,
  InterviewLinkedFilter,
  STAGE_TYPE_PRESENCE_OPTIONS,
  StageTypePresence,
} from './ProjectsFilterModal.constants';

export const STAGE_TYPE_FILTER_ROW_DATA_TEST_ID = 'stage-type-filter-row';

const StageTypeFilterRow = ({
  category,
}: {
  category: FilterableStageCategory;
}) => {
  const { values, setFieldValue } = useFormikContext<FiltersFormValues>();
  const stageTypeFilter = values.stageTypeFilters[category];
  const label = STAGE_CATEGORY_LABELS[category];
  const isHas = stageTypeFilter.presence === StageTypePresence.Has;
  // Only candidates on an Online assessment carry a status; an AI interview is
  // never included.
  const hasAssessmentStatus = category === ASSESSMENT_STATUS_CATEGORY;
  // Only candidates on an Interview stage can have an interview linked to them.
  const hasInterviewLinked = category === INTERVIEW_LINKED_CATEGORY;

  const handlePresenceChange = (presence: StageTypePresence) => {
    const isStayingOnHas = presence === StageTypePresence.Has;

    setFieldValue(`stageTypeFilters.${category}`, {
      presence,
      // Anything but Has hides the detail line, so both fields are cleared
      // rather than left to reappear the next time Has is picked.
      minCandidates: isStayingOnHas ? stageTypeFilter.minCandidates : '',
      assessmentStatus: isStayingOnHas
        ? stageTypeFilter.assessmentStatus
        : AssessmentStatusFilter.Any,
      interviewLinked: isStayingOnHas
        ? stageTypeFilter.interviewLinked
        : InterviewLinkedFilter.Any,
    });
  };

  const selectedAssessmentStatus =
    ASSESSMENT_STATUS_OPTIONS.find(
      (statusOption) => statusOption.value === stageTypeFilter.assessmentStatus,
    ) ?? ASSESSMENT_STATUS_OPTIONS[0];

  return (
    <div
      className={styles.stageTypeRow}
      data-testid={`${STAGE_TYPE_FILTER_ROW_DATA_TEST_ID}-${category}`}
    >
      <div className={styles.stageTypeRowHeader}>
        <span className={styles.stageTypeRowLabel}>
          <StageTypeChip category={category} />
          {label}
        </span>
        <SegmentedControl.Root
          size="small"
          value={stageTypeFilter.presence}
          onValueChange={(value) =>
            handlePresenceChange(value as StageTypePresence)
          }
          aria-label={`${label} stages`}
        >
          {STAGE_TYPE_PRESENCE_OPTIONS.map((presenceOption) => (
            <SegmentedControl.Item
              key={presenceOption.value}
              value={presenceOption.value}
            >
              {presenceOption.label}
            </SegmentedControl.Item>
          ))}
        </SegmentedControl.Root>
      </div>

      {isHas && (
        <div className={styles.stageTypeRowDetail}>
          <span className={styles.stageTypeRowDetailText}>with at least</span>
          <div className={styles.stageTypeRowDetailInput}>
            <FormikTextInputField
              id={`stageTypeFilters.${category}.minCandidates`}
              label="Min candidates"
              ariaLabel={`${label}: minimum candidates`}
              useFormikField={useField}
              placeholder="any number of"
            />
          </div>
          <span className={styles.stageTypeRowDetailText}>candidates</span>

          {hasInterviewLinked && (
            <>
              <span className={styles.stageTypeRowDetailText}>
                who are linked to an interview
              </span>
              <SegmentedControl.Root
                size="small"
                value={stageTypeFilter.interviewLinked}
                onValueChange={(value) =>
                  setFieldValue(
                    `stageTypeFilters.${category}.interviewLinked`,
                    value as InterviewLinkedFilter,
                  )
                }
                aria-label={`${label}: linked to an interview`}
              >
                {INTERVIEW_LINKED_OPTIONS.map((interviewLinkedOption) => (
                  <SegmentedControl.Item
                    key={interviewLinkedOption.value}
                    value={interviewLinkedOption.value}
                  >
                    {interviewLinkedOption.label}
                  </SegmentedControl.Item>
                ))}
              </SegmentedControl.Root>
            </>
          )}

          {hasAssessmentStatus && (
            <>
              <span className={styles.stageTypeRowDetailText}>
                whose assessment status is
              </span>
              <Select
                id={`stageTypeFilters.${category}.assessmentStatus`}
                className={styles.stageTypeRowSelect}
                options={ASSESSMENT_STATUS_OPTIONS}
                value={selectedAssessmentStatus}
                ariaLabel={`${label}: assessment status`}
                isSearchable={false}
                onChange={(statusOption) =>
                  setFieldValue(
                    `stageTypeFilters.${category}.assessmentStatus`,
                    statusOption.value ?? AssessmentStatusFilter.Any,
                  )
                }
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default StageTypeFilterRow;
